<template>
  <div class="w-full">
    <div class="plyr-wrapper w-full">
      <!-- Custom video  -->
      <template v-if="videoType === 'custom'">
        <vue-plyr ref="vuePlayer" :options="playerOptions">
          <video
            class="vue-plyr-video-ele"
            :data-plyr-config="playerOptions"
            playsinline
            preload="auto"
            @loadeddata="handleVideoLoaded"
            @progress="handleProgress"
            @loadedmetadata="handleMetadata"
          >
            <source :src="optimizedVideoUrl" type="video/mp4" />
          </video>
        </vue-plyr>
      </template>

      <!-- Youtube Video -->
      <template v-else>
        <vue-plyr ref="vuePlayer" :options="playerOptions">
          <div
            data-plyr-provider="youtube"
            :data-plyr-embed-id="videoId"
            :data-plyr-config="playerOptions"
          ></div>
        </vue-plyr>
      </template>
    </div>
    <div class="hidden">
      <CustomControls
        ref="customControls"
        :player="player"
        :customControlDiv="customControlDiv"
        @pauseVideo="controlVideo"
        @playVideo="controlVideo"
        @toggleVideo="controlVideo"
        @exitFullScreen="controlVideo"
      />
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  defineProps,
  defineExpose,
} from "vue";
import VuePlyr from "@skjnldsv/vue-plyr";
import "@skjnldsv/vue-plyr/dist/vue-plyr.css";
import CustomControls from "./CustomControls.vue";

// const controls = `
// <div class="plyr__controls" style="flex-direction: column" id="player-controls-div">
//   <div class="w-full flex justify-evenly items-center">
//     <button type="button" class="plyr__control" data-plyr="rewind">
//         <svg role="presentation"><use xlink:href="#plyr-rewind"></use></svg>
//         <span class="plyr__tooltip" role="tooltip">Rewind {seektime} secs</span>
//     </button>
//     <button type="button" class="plyr__control" aria-label="Play, {title}" data-plyr="play">
//         <svg class="icon--pressed" role="presentation"><use xlink:href="#plyr-pause"></use></svg>
//         <svg class="icon--not-pressed" role="presentation"><use xlink:href="#plyr-play"></use></svg>
//         <span class="label--pressed plyr__tooltip" role="tooltip">Pause</span>
//         <span class="label--not-pressed plyr__tooltip" role="tooltip">Play</span>
//     </button>
//     <button type="button" class="plyr__control" data-plyr="fast-forward">
//         <svg role="presentation"><use xlink:href="#plyr-fast-forward"></use></svg>
//         <span class="plyr__tooltip" role="tooltip">Forward {seektime} secs</span>
//     </button>
//     <div class="plyr__progress">
//         <input style="color: #779aa9" data-plyr="seek" type="range" min="0" max="100" step="0.01" value="0" aria-label="Seek">
//         <progress class="plyr__progress__buffer" min="0" max="100" value="0">% buffered</progress>
//         <span role="tooltip" class="plyr__tooltip">00:00</span>
//     </div>
//     <div class="plyr__time plyr__time--current" aria-label="Current time">00:00</div>
//     <div class="plyr__time plyr__time--duration" aria-label="Duration">00:00</div>
//     <button type="button" class="plyr__control" aria-label="Mute" data-plyr="mute">
//         <svg class="icon--pressed" role="presentation"><use xlink:href="#plyr-muted"></use></svg>
//         <svg class="icon--not-pressed" role="presentation"><use xlink:href="#plyr-volume"></use></svg>
//         <span class="label--pressed plyr__tooltip" role="tooltip">Unmute</span>
//         <span class="label--not-pressed plyr__tooltip" role="tooltip">Mute</span>
//     </button>
//     <div class="plyr__volume">
//         <input style="color: #779aa9" data-plyr="volume" type="range" min="0" max="1" step="0.05" value="1" autocomplete="off" aria-label="Volume">
//     </div>

// </div>
// </div>
// `;

{
  /* <button type="button" class="plyr__control" data-plyr="fullscreen">
        <svg class="icon--pressed" role="presentation"><use xlink:href="#plyr-exit-fullscreen"></use></svg>
        <svg class="icon--not-pressed" role="presentation"><use xlink:href="#plyr-enter-fullscreen"></use></svg>
        <span class="label--pressed plyr__tooltip" role="tooltip">Exit fullscreen</span>
        <span class="label--not-pressed plyr__tooltip" role="tooltip">Enter fullscreen</span>
    </button> */
}

// Props definition
const props = defineProps({
  videoUrl: {
    type: String,
    default: "",
  },
  videoType: {
    type: String,
    default: "youtube",
  },
});

// Refs
const vuePlayer = ref(null);
const customControls = ref(null);
const player = ref(null);
const customControlDiv = ref(null);
const timeout = ref(null);
const isLoading = ref(true);
const bufferingProgress = ref(0);

// Player options
const playerOptions = {
  ratio: "16:8",
  autoplay: true,
  playsinline: true,
  clickToPlay: true,
  loadSprite: true,
  preload: "metadata", // Changed from auto to metadata for faster initial load
  fullscreen: {
    enabled: false,
    fallback: true,
    iosNative: false,
    container: null,
  },
  seekTime: 10,
  volume: 1,
  keyboard: { focused: true, global: true },
  youtube: {
    autoplay: 1,
    noCookie: false,
    showinfo: 0,
    modestbranding: 1,
    playsinline: 1,
    rel: 0,
    origin: window.location.origin,
    iv_load_policy: 3,
    cc_load_policy: 0,
    enablejsapi: 1,
    controls: 0,
  },
  controls: [
    "play-large",
    "rewind",
    "play",
    "fast-forward",
    "progress",
    "current-time",
    "duration",
    "mute",
    "volume",
    "settings",
    // "fullscreen", // Toggle fullscreen
  ],
};

// Computed properties
const videoId = computed(() => {
  const url = props.videoUrl;
  const regex =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
});

// Methods
const controlVideo = (action) => {
  const playerInstance = vuePlayer.value?.player;

  if (!playerInstance) {
    console.error("Player not initialized");
    return;
  }

  switch (action) {
    case "play":
      console.log("Control video | Action : Play");
      vuePlayer.value.player?.play();
      break;
    case "pause":
      console.log("Control video | Action : Pause");
      vuePlayer.value.player?.pause();
      break;
    case "exitFullScreen":
      console.log("Control video | Action : ExitFullScreen");
      if (vuePlayer.value.player?.fullscreen?.active)
        vuePlayer.value.player?.fullscreen?.exit();
      break;
    default:
      console.log("Control video | Action : Invalid");
      break;
  }
};

const handleVideoLoaded = () => {
  console.log("Video data loaded");
  isLoading.value = false;

  // Force a redraw of the video element
  const videoElement = document.querySelector("video");
  if (videoElement) {
    videoElement.style.display = "none";
    setTimeout(() => {
      videoElement.style.display = "block";
    }, 50);
  }
};

// Add these new variables for S3 optimization
const lastBufferCheck = ref(Date.now());
const rangeRequestsSupported = ref(true);
const s3VideoQuality = ref("auto");
// const videoStartTime = ref(0);

// Update the optimizedVideoUrl computed property
const optimizedVideoUrl = computed(() => {
  if (!props.videoUrl || props.videoType !== "custom") {
    return props.videoUrl;
  }

  // For S3 presigned URLs, we should not modify the URL as it will invalidate the signature
  // Just return the original URL to maintain the signature validity
  if (
    props.videoUrl.includes("AWSAccessKeyId") ||
    props.videoUrl.includes("Signature")
  ) {
    return props.videoUrl;
  }

  // For non-presigned URLs, we can add optimization parameters
  const separator = props.videoUrl.includes("?") ? "&" : "?";
  let url = `${props.videoUrl}${separator}_cb=${Date.now()}`;

  return url;
});

// Modify the checkS3Capabilities function to handle presigned URLs properly
const checkS3Capabilities = async () => {
  if (props.videoType !== "custom" || !props.videoUrl) return;

  // For presigned URLs, we should not send additional headers
  if (
    props.videoUrl.includes("AWSAccessKeyId") ||
    props.videoUrl.includes("Signature")
  ) {
    console.log(
      "Using presigned S3 URL - skipping capability checks to preserve signature"
    );
    rangeRequestsSupported.value = true; // Assume supported
    return;
  }

  try {
    // Test if range requests are supported
    const response = await fetch(props.videoUrl, {
      method: "HEAD",
      headers: {
        Range: "bytes=0-0",
      },
    });

    rangeRequestsSupported.value =
      response.headers.get("Accept-Ranges") === "bytes";
    console.log(`S3 range requests supported: ${rangeRequestsSupported.value}`);

    // If range requests are supported, we can optimize streaming
    if (rangeRequestsSupported.value) {
      preloadVideoChunks();
    }
  } catch (error) {
    console.warn("Could not check S3 capabilities:", error);
  }
};

// Modify the preloadVideoChunks function to handle presigned URLs
const preloadVideoChunks = async () => {
  if (!props.videoUrl || props.videoType !== "custom") return;

  // Skip for presigned URLs to avoid signature issues
  if (
    props.videoUrl.includes("AWSAccessKeyId") ||
    props.videoUrl.includes("Signature")
  ) {
    return;
  }

  try {
    // Preload the first chunk of the video
    const response = await fetch(props.videoUrl, {
      headers: {
        Range: "bytes=0-500000", // Preload first 500KB
      },
    });

    if (response.ok) {
      console.log("Preloaded initial video chunk");
    }
  } catch (error) {
    console.warn("Failed to preload video chunk:", error);
  }
};

// Update the handleProgress function
const handleProgress = (event) => {
  const video = event.target;
  if (!video || !video.buffered || video.buffered.length === 0) return;

  // Calculate buffering progress
  const bufferedEnd = video.buffered.end(video.buffered.length - 1);
  const duration = video.duration;
  bufferingProgress.value = (bufferedEnd / duration) * 100;
  lastBufferCheck.value = Date.now();

  // Adaptive quality management based on buffer progress
  if (player.value && bufferedEnd > 0) {
    // If buffering is slow, optimize playback
    if (
      bufferingProgress.value < 10 &&
      Date.now() - lastBufferCheck.value > 3000
    ) {
      console.log("Buffering is slow, optimizing for speed");

      // Move playhead to buffered content
      if (video.currentTime < bufferedEnd - 1) {
        video.currentTime = bufferedEnd - 0.5;
      }

      // Lower quality if needed
      if (s3VideoQuality.value !== "low") {
        s3VideoQuality.value = "low";
        // Request a lower quality version if available
        requestQualityChange("low");
      }
    } else if (bufferingProgress.value > 50 && s3VideoQuality.value === "low") {
      // If buffering is good, we can increase quality
      s3VideoQuality.value = "auto";
      requestQualityChange("auto");
    }

    // If we have enough buffer, hide loading indicator
    if (bufferingProgress.value > 15 && isLoading.value) {
      isLoading.value = false;
    }
  }

  console.log(`Buffering: ${bufferingProgress.value.toFixed(2)}%`);
};

// Add this function to request quality changes
const requestQualityChange = (quality) => {
  // This would ideally communicate with your backend
  console.log(`Requesting ${quality} quality video`);

  // For now, we'll just update the player if possible
  if (player.value && player.value.quality) {
    player.value.quality = quality;
  }
};

// Update the onMounted hook to include S3 optimizations
onMounted(() => {
  // Check S3 capabilities when component mounts
  checkS3Capabilities();

  // Add event listener for network changes
  window.addEventListener("online", () => {
    if (props.videoType === "custom") {
      console.log("Network connection restored, optimizing video");
      s3VideoQuality.value = "auto";
      requestQualityChange("auto");
    }
  });

  window.addEventListener("offline", () => {
    if (props.videoType === "custom") {
      console.log("Network connection lost, reducing quality");
      s3VideoQuality.value = "low";
      requestQualityChange("low");
    }
  });

  const playerInstance = vuePlayer.value?.player;

  if (playerInstance) {
    playerInstance.on("ready", () => {
      player.value = playerInstance;
      console.log("Plyr is ready");

      if (playerInstance.media) {
        playerInstance.media.preload = "metadata";
      }

      const customControlsEl = customControls.value.$el;
      customControlsEl.id = "custom-control-div";

      // workaround for autoplay
      playerInstance.muted = true;
      // play video programmatically
      controlVideo("play");
      timeout.value = setTimeout(() => {
        if (playerInstance?.muted != null) {
          playerInstance.muted = false;
          playerInstance && playerInstance?.restart();
        }
      }, 1000);
    });

    playerInstance.on("enterfullscreen", () => {
      console.log("isFullScreenActive: ", true);
      // add mic button only on full screen mode
      const controlsDiv = document.querySelector("#player-controls-div");
      const customControlsEl = customControls.value.$el;
      customControlsEl.id = "custom-control-div";
      controlsDiv.appendChild(customControlsEl);
      customControlDiv.value = document.querySelector("#custom-control-div");
    });

    playerInstance.on("exitfullscreen", () => {
      console.log("isFullScreenActive: ", false);
      // remove mic when exiting full screen mode
      if (customControlDiv.value) customControlDiv.value.remove();
    });

    playerInstance.on("playing", () => {
      console.log("Video Playing event ");
      if (customControls.value.isPlaying) {
        customControls.value.stopAudio();
      }
    });

    playerInstance.on("play", () => {
      if (playerInstance.media) {
        playerInstance.media.preload = "auto";
      }
    });

    playerInstance.on("error", (error) => {
      console.log("custom player error: ", error);
    });
  }
});

onBeforeUnmount(() => {
  if (timeout.value) {
    clearTimeout(timeout.value);
  }

  // Remove network event listeners
  window.removeEventListener("online", () => {});
  window.removeEventListener("offline", () => {});
});

// Watch for changes to videoUrl
watch(
  () => props.videoUrl,
  (newUrl) => {
    if (newUrl && props.videoType === "custom") {
      isLoading.value = true;
    }
  },
  { immediate: true }
);

// expose methods
defineExpose({
  controlVideo,
});
</script>

<style scoped>
@property --plyr-color-main {
  syntax: "<color>";
  inherits: false;
  initial-value: #779aa9;
}
@property --plyr-video-control-background-hover {
  syntax: "<color>";
  inherits: false;
  initial-value: transparent;
}
@property --plyr-progress-loading-background {
  syntax: "<color>";
  inherits: false;
  initial-value: transparent;
}
:deep(.plyr--youtube) {
  height: 30vh !important;
}
@media (min-width: 768px) {
  :deep(.plyr--youtube) {
    height: 100% !important;
  }
}
@media (min-width: 1024px) {
  :deep(.plyr--youtube) {
    height: 70vh !important;
  }
}
@media (min-width: 1280px) {
  :deep(.plyr--youtube) {
    height: 75vh !important;
  }
}
:deep(.plyr__video-wrapper) {
  height: 30vh !important;
}
@media (min-width: 768px) {
  :deep(.plyr__video-wrapper) {
    height: 100% !important;
  }
}
@media (min-width: 1024px) {
  :deep(.plyr__video-wrapper) {
    height: 70vh !important;
  }
}
@media (min-width: 1280px) {
  :deep(.plyr__video-wrapper) {
    height: 75vh !important;
  }
}
/* Add optimizations for video loading */
:deep(.plyr__video-wrapper video) {
  object-fit: contain;
}

:deep(.plyr--youtube iframe) {
  visibility: visible !important;
}

/* Loading indicator styles */
.video-loading-indicator {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  z-index: 5;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 5px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #779aa9;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Ensure video is visible */
:deep(.plyr__video-wrapper video) {
  object-fit: contain;
  min-height: 30vh;
}

/* Improve buffering visualization */
:deep(.plyr__progress__buffer) {
  background-color: rgba(119, 154, 169, 0.3) !important;
}

/* Ensure YouTube iframe is visible */
:deep(.plyr--youtube iframe) {
  visibility: visible !important;
}

/* Enhance loading indicator with buffer progress */
.video-loading-indicator::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  height: 4px;
  background-color: #779aa9;
  width: v-bind(bufferingProgress + "%");
  transition: width 0.3s ease;
}

/* Optimize video rendering */
:deep(.plyr__video-wrapper video) {
  object-fit: contain;
  min-height: 30vh;
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
}
</style>
